/**
 * Blog list carousel. Initializes Swiper only when the section enables it.
 * cssMode keeps native trackpad/wheel; pointer drag covers Windows mouse.
 */
(function () {
  'use strict';

  const SELECTOR = {
    viewport: '.blog-list__viewport',
    track: '.blog-list__track',
    prev: '.blog-list__nav--prev',
    next: '.blog-list__nav--next',
  };

  /**
   * Horizontal article carousel scoped to one blog-list section.
   */
  class BlogListCarousel extends HTMLElement {
    constructor() {
      super();
      this._swiper = null;
      this._mounting = false;
      this._drag = null;
      this._onResize = this._onResize.bind(this);
      this._syncNav = this._syncNav.bind(this);
      this._onPrev = this._onNavClick.bind(this, -1);
      this._onNext = this._onNavClick.bind(this, 1);
      this._onPointerDown = this._onPointerDown.bind(this);
      this._onPointerMove = this._onPointerMove.bind(this);
      this._onPointerUp = this._onPointerUp.bind(this);
      this._onClickCapture = this._onClickCapture.bind(this);
    }

    connectedCallback() {
      if (this._swiper || this._mounting) {
        return;
      }

      this._viewport = this.querySelector(SELECTOR.viewport);
      this._track = this.querySelector(SELECTOR.track);
      this._prev = this.querySelector(SELECTOR.prev);
      this._next = this.querySelector(SELECTOR.next);
      this._section = this.closest('.section-settings');
      if (!this._viewport || !this._track) {
        return;
      }

      this._applyOffsets();
      this._bindChrome();
      this._syncNav();
      this._mounting = true;
      window
        .loadSwiper()
        .then(() => {
          if (!this.isConnected) {
            this._mounting = false;
            return;
          }
          this._init();
        })
        .catch((error) => {
          this._mounting = false;
          this.removeAttribute('aria-busy');
          this._syncNav();
          console.error('[blog-list] Unable to load Swiper.', error);
        });
    }

    disconnectedCallback() {
      this._destroy();
    }

    /**
     * MO / TA 32 / PC min gutter from the resolved token.
     * @param {CSSStyleDeclaration} styles
     * @returns {number}
     */
    _getMinMargin(styles) {
      const resolved = parseFloat(styles.getPropertyValue('--container-horizontal-min-margin'));
      if (Number.isFinite(resolved)) {
        return resolved;
      }

      const isMobile = window.matchMedia('(max-width: 767px)').matches;
      if (isMobile) {
        return parseFloat(styles.getPropertyValue('--container-horizontal-min-margin-mobile')) || 0;
      }

      const isTa = window.matchMedia('(min-width: 768px) and (max-width: 1279px)').matches;
      const token = isTa
        ? '--container-horizontal-min-margin-ta'
        : '--container-horizontal-min-margin-pc';
      return parseFloat(styles.getPropertyValue(token)) || 0;
    }

    /**
     * Left inset that lines the first card up with the 1200 content column.
     * @returns {number}
     */
    _getStartOffset() {
      const styles = getComputedStyle(this._section || this);
      const isMobile = window.matchMedia('(max-width: 767px)').matches;
      const minMargin = this._getMinMargin(styles);
      if (isMobile) {
        return minMargin;
      }

      const contentWidth = parseFloat(styles.getPropertyValue('--current-section-width')) || 1200;
      const viewportWidth = this._viewport ? this._viewport.clientWidth : window.innerWidth;
      return Math.max(minMargin, (viewportWidth - contentWidth) / 2);
    }

    /**
     * Trailing inset so the last card can rest near the viewport edge.
     * @returns {number}
     */
    _getEndOffset() {
      const styles = getComputedStyle(this._section || this);
      return this._getMinMargin(styles);
    }

    /**
     * Write 1200 gutters as CSS variables. cssMode does not paint slidesOffsetBefore.
     */
    _applyOffsets() {
      this.style.setProperty('--blog-list-offset-start', `${this._getStartOffset()}px`);
      this.style.setProperty('--blog-list-offset-end', `${this._getEndOffset()}px`);
    }

    /**
     * Card gap from CSS token. MO is 12 (--space-token-s), TA/PC stay 20.
     * @returns {number}
     */
    _getGap() {
      const styles = getComputedStyle(this);
      const gap = parseFloat(styles.getPropertyValue('--blog-list-gap'));
      return Number.isFinite(gap) ? gap : 20;
    }

    /**
     * Native CSS scroll (cssMode) so PC trackpad/wheel matches overflow:scroll.
     * freeMode here only turns off scroll-snap; momentum is the browser's.
     */
    _init() {
      if (this._swiper || !this._viewport || typeof window.Swiper !== 'function') {
        this._mounting = false;
        this.removeAttribute('aria-busy');
        this._syncNav();
        return;
      }

      this._applyOffsets();
      this._swiper = new window.Swiper(this._viewport, {
        slidesPerView: 'auto',
        spaceBetween: this._getGap(),
        speed: 500,
        cssMode: true,
        freeMode: {
          enabled: true,
        },
        simulateTouch: false,
        watchOverflow: false,
        navigation: false,
        observer: true,
        observeParents: true,
        on: {
          init: this._syncNav,
          resize: this._syncNav,
          slideChange: this._syncNav,
          progress: this._syncNav,
          setTranslate: this._syncNav,
          transitionEnd: this._syncNav,
        },
      });

      this.setAttribute('data-swiper-mounted', '');
      this.removeAttribute('aria-busy');
      this._mounting = false;
      window.addEventListener('resize', this._onResize, { passive: true });
      this._syncNav();
    }

    /**
     * Recalculate 1200-aligned offsets after viewport changes.
     */
    _onResize() {
      this._applyOffsets();
      if (!this._swiper) {
        this._syncNav();
        return;
      }
      this._swiper.params.spaceBetween = this._getGap();
      this._swiper.update();
      this._syncNav();
    }

    /**
     * The element that actually scrolls in cssMode.
     * @returns {HTMLElement|null}
     */
    _scrollEl() {
      return this._swiper && this._swiper.wrapperEl ? this._swiper.wrapperEl : this._track;
    }

    /**
     * Bind nav clicks, native scroll sync, and mouse drag (Windows).
     */
    _bindChrome() {
      if (this._prev) {
        this._prev.addEventListener('click', this._onPrev);
      }
      if (this._next) {
        this._next.addEventListener('click', this._onNext);
      }
      this._track.addEventListener('scroll', this._syncNav, { passive: true });
      this.addEventListener('pointerdown', this._onPointerDown, true);
      this.addEventListener('mousedown', this._onPointerDown, true);
      this.addEventListener('click', this._onClickCapture, true);
    }

    /**
     * @param {number} direction -1 prev, 1 next
     * @param {MouseEvent} [event]
     */
    _onNavClick(direction, event) {
      if (event) {
        event.preventDefault();
      }
      const scroller = this._scrollEl();
      if (!scroller) {
        return;
      }
      const slide = this.querySelector('.blog-list__slide');
      const step = (slide ? slide.getBoundingClientRect().width : 484) + this._getGap();
      scroller.scrollBy({ left: direction * step, behavior: 'smooth' });
    }

    /**
     * Mouse / pen drag-to-scroll. Touch keeps native overflow so iOS/Android
     * are not fighting a JS grab.
     * @param {PointerEvent} event
     */
    _onPointerDown(event) {
      if (this._drag) {
        return;
      }
      if (event.pointerType === 'touch' || (event.button != null && event.button !== 0)) {
        return;
      }
      if (event.target && event.target.closest && event.target.closest('.blog-list__nav')) {
        return;
      }
      const scroller = this._scrollEl();
      if (!scroller) {
        return;
      }
      this._drag = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startScroll: scroller.scrollLeft,
        moved: 0,
        scroller,
      };
      this.setAttribute('data-dragging', '');
      window.addEventListener('pointermove', this._onPointerMove, true);
      window.addEventListener('pointerup', this._onPointerUp, true);
      window.addEventListener('pointercancel', this._onPointerUp, true);
      window.addEventListener('mousemove', this._onPointerMove, true);
      window.addEventListener('mouseup', this._onPointerUp, true);
    }

    /**
     * @param {PointerEvent} event
     */
    _onPointerMove(event) {
      if (!this._drag) {
        return;
      }
      if (event.pointerId != null && this._drag.pointerId != null && event.pointerId !== this._drag.pointerId) {
        return;
      }
      const dx = event.clientX - this._drag.startX;
      this._drag.moved = Math.max(this._drag.moved, Math.abs(dx));
      this._drag.scroller.scrollLeft = this._drag.startScroll - dx;
      if (this._drag.moved > 3) {
        event.preventDefault();
      }
    }

    /**
     * @param {PointerEvent} event
     */
    _onPointerUp(event) {
      if (!this._drag) {
        return;
      }
      if (event.pointerId != null && this._drag.pointerId != null && event.pointerId !== this._drag.pointerId) {
        return;
      }
      const { moved } = this._drag;
      window.removeEventListener('pointermove', this._onPointerMove, true);
      window.removeEventListener('pointerup', this._onPointerUp, true);
      window.removeEventListener('pointercancel', this._onPointerUp, true);
      window.removeEventListener('mousemove', this._onPointerMove, true);
      window.removeEventListener('mouseup', this._onPointerUp, true);
      this._suppressClick = moved > 5;
      this._drag = null;
      this.removeAttribute('data-dragging');
      this._syncNav();
    }

    /**
     * Swallow the click that follows a drag so article links do not fire.
     * @param {MouseEvent} event
     */
    _onClickCapture(event) {
      if (!this._suppressClick) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      this._suppressClick = false;
    }

    /**
     * Toggle nav visibility from the real overflow state, not Swiper isLocked.
     */
    _syncNav() {
      const scroller = this._scrollEl();
      if (!scroller) {
        return;
      }

      const maxScroll = scroller.scrollWidth - scroller.clientWidth;
      const hasOverflow = maxScroll > 1;
      const scrollLeft = scroller.scrollLeft;
      this.toggleAttribute('data-has-overflow', hasOverflow);

      this._setNavDisabled(this._prev, !hasOverflow || scrollLeft <= 1);
      this._setNavDisabled(this._next, !hasOverflow || scrollLeft >= maxScroll - 1);
    }

    /**
     * @param {HTMLElement|null} button
     * @param {boolean} disabled
     */
    _setNavDisabled(button, disabled) {
      if (!button) {
        return;
      }
      button.toggleAttribute('data-hidden', disabled);
      button.classList.toggle('is-disabled', disabled);
      button.setAttribute('aria-disabled', String(disabled));
      button.disabled = disabled;
    }

    /**
     * Destroy Swiper and listeners so the next connect can retry.
     */
    _destroy() {
      window.removeEventListener('resize', this._onResize);
      if (this._prev) {
        this._prev.removeEventListener('click', this._onPrev);
      }
      if (this._next) {
        this._next.removeEventListener('click', this._onNext);
      }
      if (this._track) {
        this._track.removeEventListener('scroll', this._syncNav);
      }
      this.removeEventListener('pointerdown', this._onPointerDown, true);
      this.removeEventListener('mousedown', this._onPointerDown, true);
      this.removeEventListener('click', this._onClickCapture, true);
      window.removeEventListener('pointermove', this._onPointerMove, true);
      window.removeEventListener('pointerup', this._onPointerUp, true);
      window.removeEventListener('pointercancel', this._onPointerUp, true);
      window.removeEventListener('mousemove', this._onPointerMove, true);
      window.removeEventListener('mouseup', this._onPointerUp, true);
      if (this._swiper) {
        this._swiper.destroy(true, true);
        this._swiper = null;
      }
      this._drag = null;
      this._mounting = false;
      this.removeAttribute('data-swiper-mounted');
      this.removeAttribute('data-has-overflow');
      this.removeAttribute('data-dragging');
    }
  }

  if (!window.customElements.get('blog-list-carousel')) {
    window.customElements.define('blog-list-carousel', BlogListCarousel);
  }
})();
